import React, { useEffect, useRef } from 'react';
type MicWaveformProps = {
  bars?: number;
  className?: string;
};
/** Deterministic pseudo-random heights so renders stay stable */
function seededHeights(count: number): number[] {
  const heights: number[] = [];
  let seed = 7;
  for (let i = 0; i < count; i++) {
    seed = seed * 16807 % 2147483647;
    heights.push(0.25 + seed % 1000 / 1000 * 0.75);
  }
  return heights;
}
/**
 * Waveform that leans toward the cursor like a microphone — bars near the
 * pointer's x-position rise and brighten. Desktop only; static on touch.
 * Direct style writes on rAF keep it cheap (no React re-renders).
 */
export function MicWaveform({
  bars = 64,
  className = ''
}: MicWaveformProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const barRefs = useRef<(HTMLSpanElement | null)[]>([]);
  const target = useRef<number | null>(null);
  const raf = useRef(0);
  useEffect(() => {
    if (window.matchMedia('(pointer: coarse)').matches) return;
    const section = containerRef.current?.closest('section');
    if (!section) return;
    const render = () => {
      const rect = containerRef.current?.getBoundingClientRect();
      if (!rect) return;
      barRefs.current.forEach((bar, i) => {
        if (!bar) return;
        const barX = rect.left + (i + 0.5) / bars * rect.width;
        let boost = 0;
        if (target.current !== null) {
          const dist = Math.abs(target.current - barX);
          boost = Math.max(0, 1 - dist / 300);
        }
        bar.style.transform = `scaleY(${1 + boost * 0.4})`;
        bar.style.opacity = `${0.35 + boost * 0.35}`;
      });
    };
    const onMove = (e: MouseEvent) => {
      target.current = e.clientX;
      cancelAnimationFrame(raf.current);
      raf.current = requestAnimationFrame(render);
    };
    const onLeave = () => {
      target.current = null;
      cancelAnimationFrame(raf.current);
      raf.current = requestAnimationFrame(render);
    };
    section.addEventListener('mousemove', onMove);
    section.addEventListener('mouseleave', onLeave);
    return () => {
      section.removeEventListener('mousemove', onMove);
      section.removeEventListener('mouseleave', onLeave);
      cancelAnimationFrame(raf.current);
    };
  }, [bars]);
  const heights = seededHeights(bars);
  return <div ref={containerRef} className={`flex items-end gap-[3px] ${className}`} aria-hidden="true" data-id="element-307">
      {heights.map((h, i) => <span key={i} ref={el => {
      barRefs.current[i] = el;
    }} className="w-[3px] flex-none origin-bottom rounded-full bg-teal-500/30 will-change-transform" style={{
      height: `${h * 100}%`,
      opacity: 0.35,
      transition: 'transform 0.25s ease-out, opacity 0.25s ease-out'
    }} data-id="element-308" />)}
    </div>;
}